import { calendarDateType } from "../../ControlPanel/_shared";
import type {
  ChartCustomRefLine,
  MetricOption,
  ScatterplotAxisDef,
  ScatterplotComponentSpec,
  ScatterplotEntityGroupBy,
  ScatterplotTrendlineScope,
  ScatterplotXAxisMode,
  SubjectEntity,
} from "../../Processing/types";
import type { DateRangeFormFields } from "../shared/DateRangeForm";
import type { PlayersFormFields } from "../shared/PlayersForm";
import { DATE_RANGE_DEFAULTS } from "../shared/DateRangeForm";
import { PLAYERS_DEFAULTS, resolveSubjectNames } from "../shared/PlayersForm";

// ── State ──

export interface ScatterplotAxisFormState {
  queryId: string;
  metricId: string | null;
  metricAgg: "avg" | "sum" | "best";
  /** Metric display name captured at selection time (spec displayName). */
  displayName: string;
  /** User-supplied axis label; "" = use displayName. */
  displayNameOverride: string;
  /** Reference lines drawn on this axis (value, optional label, color). */
  plotLines: ChartCustomRefLine[];
}

export interface ScatterplotFormState extends DateRangeFormFields, PlayersFormFields {
  title: string;
  showLegend: boolean;
  xAxisMode: ScatterplotXAxisMode;
  groupBy: ScatterplotEntityGroupBy;
  teamAverages: boolean;
  groupByGameDate: boolean;
  showTrendline: boolean;
  trendlineScope: ScatterplotTrendlineScope;
  showPointLabels: boolean;
  showPointDates: boolean;
  excludeInjuredPlayers: boolean;
  xAxis: ScatterplotAxisFormState;
  yAxis: ScatterplotAxisFormState;
  expandedKey: string | string[];
}

export type ScatterplotAxisKey = "xAxis" | "yAxis";

/** What one dot is: an entity per day, or the whole selection per day (teamAverages). */
export type ScatterplotDotGrouping = ScatterplotEntityGroupBy | "team";

// ── Actions ──

export type ScatterplotAction =
  | { type: "SET_TITLE"; title: string }
  | { type: "SET_SHOW_LEGEND"; value: boolean }
  | { type: "SET_X_AXIS_MODE"; value: ScatterplotXAxisMode }
  | { type: "SET_DOT_GROUPING"; value: ScatterplotDotGrouping }
  | { type: "SET_GROUP_BY_GAME_DATE"; value: boolean }
  | { type: "SET_SHOW_TRENDLINE"; value: boolean }
  | { type: "SET_TRENDLINE_SCOPE"; value: ScatterplotTrendlineScope }
  | { type: "SET_SHOW_POINT_LABELS"; value: boolean }
  | { type: "SET_SHOW_POINT_DATES"; value: boolean }
  | { type: "SET_EXCLUDE_INJURED_PLAYERS"; value: boolean }
  | { type: "UPDATE_AXIS"; axis: ScatterplotAxisKey; patch: Partial<Omit<ScatterplotAxisFormState, "queryId">> }
  | { type: "SET_EXPANDED_KEY"; key: string | string[] }
  | { type: "UPDATE_DATE_RANGE"; patch: Partial<DateRangeFormFields> }
  | { type: "UPDATE_PLAYERS"; patch: Partial<PlayersFormFields> };

// ── Reducer ──

export function scatterplotReducer(
  state: ScatterplotFormState,
  action: ScatterplotAction,
): ScatterplotFormState {
  switch (action.type) {
    case "SET_TITLE":
      return { ...state, title: action.title };
    case "SET_SHOW_LEGEND":
      return { ...state, showLegend: action.value };
    case "SET_X_AXIS_MODE":
      return { ...state, xAxisMode: action.value };
    case "SET_DOT_GROUPING":
      return action.value === "team"
        ? { ...state, teamAverages: true }
        : { ...state, teamAverages: false, groupBy: action.value };
    case "SET_GROUP_BY_GAME_DATE":
      return { ...state, groupByGameDate: action.value };
    case "SET_SHOW_TRENDLINE":
      return { ...state, showTrendline: action.value };
    case "SET_TRENDLINE_SCOPE":
      return { ...state, trendlineScope: action.value };
    case "SET_SHOW_POINT_LABELS":
      return { ...state, showPointLabels: action.value };
    case "SET_SHOW_POINT_DATES":
      return { ...state, showPointDates: action.value };
    case "SET_EXCLUDE_INJURED_PLAYERS":
      return { ...state, excludeInjuredPlayers: action.value };
    case "UPDATE_AXIS":
      return { ...state, [action.axis]: { ...state[action.axis], ...action.patch } };
    case "SET_EXPANDED_KEY":
      return { ...state, expandedKey: action.key };
    case "UPDATE_DATE_RANGE":
      return { ...state, ...action.patch };
    case "UPDATE_PLAYERS":
      return { ...state, ...action.patch };
    default:
      return state;
  }
}

// ── Factories ──

function createAxisState(): ScatterplotAxisFormState {
  return {
    queryId: crypto.randomUUID(),
    metricId: null,
    metricAgg: "avg",
    displayName: "",
    displayNameOverride: "",
    plotLines: [],
  };
}

export function createInitialState(): ScatterplotFormState {
  return {
    title: "",
    showLegend: true,
    xAxisMode: "metric",
    groupBy: "player",
    teamAverages: false,
    groupByGameDate: false,
    showTrendline: false,
    trendlineScope: "all",
    showPointLabels: false,
    showPointDates: false,
    excludeInjuredPlayers: true,
    xAxis: createAxisState(),
    yAxis: createAxisState(),
    expandedKey: ["__date_range"],
    ...DATE_RANGE_DEFAULTS,
    ...PLAYERS_DEFAULTS,
  };
}

function hydrateAxis(axis: ScatterplotAxisDef | null | undefined): ScatterplotAxisFormState {
  if (!axis) return createAxisState();
  return {
    queryId: axis.queryId,
    metricId: axis.axis_builder_misc?.selectedMetricId ?? null,
    metricAgg: axis.metricAgg ?? "avg",
    displayName: axis.displayName ?? "",
    displayNameOverride: axis.displayNameOverride ?? "",
    plotLines: (axis.plotLines ?? []).filter((l) => Number.isFinite(l.value)),
  };
}

export function hydrateFromSpec(spec: ScatterplotComponentSpec): ScatterplotFormState {
  const misc = spec.scatterplot_builder_misc ?? {};
  const mutations = spec.widgetMutations ?? {};
  return {
    title: spec.display.title ?? "",
    showLegend: spec.display.showLegend !== false,
    xAxisMode: spec.xAxisMode ?? "metric",
    groupBy: misc.groupBy ?? "player",
    teamAverages: mutations.teamAverages === true,
    groupByGameDate: mutations.groupByGameDate === true,
    showTrendline: mutations.showTrendline === true,
    trendlineScope: mutations.trendlineScope ?? "all",
    showPointLabels: mutations.showPointLabels === true,
    showPointDates: mutations.showPointDates === true,
    excludeInjuredPlayers: misc.excludeInjuredPlayers ?? true,
    xAxis: hydrateAxis(spec.xAxis),
    yAxis: hydrateAxis(spec.yAxis),
    expandedKey: ["__date_range"],
    dateRange: misc.dateRange ?? DATE_RANGE_DEFAULTS.dateRange,
    showAdvancedDate: misc.showAdvancedDate ?? DATE_RANGE_DEFAULTS.showAdvancedDate,
    startDateType: misc.startDateType ?? DATE_RANGE_DEFAULTS.startDateType,
    startDateAbsolute: misc.startDateAbsolute ?? DATE_RANGE_DEFAULTS.startDateAbsolute,
    startDateOffset: misc.startDateOffset ?? DATE_RANGE_DEFAULTS.startDateOffset,
    endDateType: misc.endDateType ?? DATE_RANGE_DEFAULTS.endDateType,
    endDateAbsolute: misc.endDateAbsolute ?? DATE_RANGE_DEFAULTS.endDateAbsolute,
    endDateOffset: misc.endDateOffset ?? DATE_RANGE_DEFAULTS.endDateOffset,
    dayFilter: misc.dayFilter ?? DATE_RANGE_DEFAULTS.dayFilter,
    selectedSubjects: misc.selectedSubjects ?? PLAYERS_DEFAULTS.selectedSubjects,
    selectedDrills: misc.selectedDrills ?? PLAYERS_DEFAULTS.selectedDrills,
    subjectActiveTab: misc.subjectActiveTab ?? PLAYERS_DEFAULTS.subjectActiveTab,
  };
}

// ── Validation ──

const isGpsMetric = (metricId: string | null) => !!metricId && metricId.startsWith("gps-");

/** Human-readable reason the current state cannot be saved, or null when valid.
 *  Mirrors the backend WidgetSpec validators so a spec the FE emits always saves. */
export function specBlockReason(state: ScatterplotFormState): string | null {
  if (!state.yAxis.metricId) return "Pick a Y axis metric.";
  if (state.xAxisMode === "metric") {
    if (!state.xAxis.metricId) return "Pick an X axis metric.";
    if (
      state.xAxis.metricId === state.yAxis.metricId &&
      state.xAxis.metricAgg === state.yAxis.metricAgg
    ) {
      return "The X and Y metrics must differ (a different aggregation also counts).";
    }
  }
  if (!state.teamAverages && state.groupBy === "drill") {
    const metricIds = state.xAxisMode === "metric"
      ? [state.xAxis.metricId, state.yAxis.metricId]
      : [state.yAxis.metricId];
    if (!metricIds.every(isGpsMetric)) {
      return "One dot per drill needs GPS metrics on both axes (drill data holds GPS values only).";
    }
  }
  return null;
}

// ── Emit ──

function axisToDef(
  axis: ScatterplotAxisFormState,
  metrics: MetricOption[],
): ScatterplotAxisDef {
  const resolvedName =
    axis.displayName ||
    metrics.find((m) => m.id === axis.metricId)?.name ||
    axis.metricId ||
    "";
  return {
    queryId: axis.queryId,
    displayName: resolvedName,
    ...(axis.displayNameOverride.trim() && { displayNameOverride: axis.displayNameOverride.trim() }),
    metricIdentifierType: "metric",
    metricAgg: axis.metricAgg,
    plotLines: axis.plotLines,
    axis_builder_misc: { selectedMetricId: axis.metricId },
  };
}

export function formStateToSpec(
  state: ScatterplotFormState,
  metrics: MetricOption[],
  getSubjectEntities?: (t: string) => SubjectEntity[],
): ScatterplotComponentSpec | null {
  if (specBlockReason(state) !== null) return null;

  const selectedSubjectNames = getSubjectEntities
    ? resolveSubjectNames(state.selectedSubjects, state.selectedDrills, getSubjectEntities)
    : [];

  return {
    type: "scatterplot",
    display: { title: state.title || "", showLegend: state.showLegend },
    xAxisMode: state.xAxisMode,
    xAxis: state.xAxisMode === "date" ? null : axisToDef(state.xAxis, metrics),
    yAxis: axisToDef(state.yAxis, metrics),
    widgetMutations: {
      ...(state.teamAverages && { teamAverages: true }),
      ...(state.groupByGameDate && { groupByGameDate: true }),
      ...(state.showTrendline && { showTrendline: true, trendlineScope: state.trendlineScope }),
      ...(state.showPointLabels && { showPointLabels: true }),
      ...(state.showPointLabels && state.showPointDates && { showPointDates: true }),
    },
    scatterplot_builder_misc: {
      groupBy: state.groupBy,
      dateRange: state.dateRange,
      showAdvancedDate: state.showAdvancedDate,
      startDateType: calendarDateType(state.startDateType),
      startDateAbsolute: state.startDateAbsolute,
      startDateOffset: state.startDateOffset,
      endDateType: state.endDateType,
      endDateAbsolute: state.endDateAbsolute,
      endDateOffset: state.endDateOffset,
      dayFilter: state.dayFilter,
      selectedSubjects: state.selectedSubjects,
      selectedDrills: state.selectedDrills,
      subjectActiveTab: state.subjectActiveTab,
      selectedSubjectNames,
      excludeInjuredPlayers: state.excludeInjuredPlayers,
    },
  };
}
